import './AddToCartButton.scss';

import React, { useContext, useMemo } from 'react';
import {
    useProductInventory,
    useProductPrice,
} from '@progymedia/gatsby-theme-boutik-ecommerce/src/context/store';

import { Button } from '@progymedia/gatsby-theme-boutik-ecommerce/src/components/Primitives';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import dayjs from 'dayjs';
import { faShoppingCart } from '@fortawesome/free-solid-svg-icons';
import { feedbackContext } from '@progymedia/gatsby-theme-boutik-ecommerce/src/context/feedback/feedbackContext';
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';
import { pushAddToCartEvent } from '@progymedia/gatsby-theme-boutik-ecommerce/src/utils/useGTM_UA';
import { useAddProductsToCartAction } from '@progymedia/gatsby-theme-boutik-ecommerce/src/context/cart/actions/useAddProductsToCartAction';
import { useCartContext } from '@progymedia/gatsby-theme-boutik-ecommerce/src/context/cart';
import { usePageLocalization } from '@sbi-international/boutik.gatsby.plugin.multilocale';
import { useTranslation } from 'react-i18next';

dayjs.extend(isSameOrAfter);

export const useAvailabilityInfo = (
    product: any,
    isDisplaySingle?: boolean // Lors qu'on arrive de ApplianceSingle
) => {
    const { currentLocale } = usePageLocalization();
    const [inventory] = useProductInventory(product.code ?? product.sku);
    
    const { t } = useTranslation('layout');

    return useMemo(() => {
        // null check
        if (!inventory)
            return {
                allowAddToCart: false,
                availabilityLabel: null,
                nextShipDate: null,
                addToCartVisible: false,
            };

        const now = dayjs();
        
        if (inventory?.stock?.status == 'in-stock') {
            const shipsTomorrow = now.hour() < 15; //ship two day if hour is past 3pm

            let nextAvailableDate = shipsTomorrow
                ? now.add(1, 'days')
                : now.add(2, 'days');

            nextAvailableDate = roundToNextWeekday(nextAvailableDate);

            const nextAvailableDateString =
                nextAvailableDate.format('YYYY-MM-DD');
            return {
                allowAddToCart: inventory?.stock?.inStock,
                availabilityLabel: t('addToCartBtn.available'),
                nextShipDate: nextAvailableDateString,
                addToCartVisible: true,
            };
        }

        if (inventory?.stock?.status == 'preorder') {
            const nextAvailableDate = roundToNextWeekday(
                dayjs(inventory.stock.nextAvailableDate)
            );

            if (nextAvailableDate.isSameOrAfter(now)) {
                const nextAvailableDateString =
                    nextAvailableDate.format('YYYY-MM-DD');
                const preString = isDisplaySingle
                    ? t('addToCartBtn.availability')
                    : t('addToCartBtn.next-availability');

                return {
                    allowAddToCart: true,
                    availabilityLabel:
                        preString + '\n' + nextAvailableDateString,
                    nextShipDate: nextAvailableDateString,
                    addToCartVisible: true,
                };
            }
        }

        const isCustomMade =
            product.attributes?.custom_made?.values[0][currentLocale];

        if (isCustomMade && isCustomMade === t('yes')) {
            let nextAvailableDate = now.add(4, 'weeks');

            nextAvailableDate = roundToNextWeekday(nextAvailableDate);

            const nextAvailableDateString =
                nextAvailableDate.format('YYYY-MM-DD');
            const preString = isDisplaySingle
                ? t('addToCartBtn.availability')
                : t('addToCartBtn.next-availability');
            return {
                allowAddToCart: inventory?.stock?.inStock || false,
                availabilityLabel: preString + '\n' + nextAvailableDateString,
                nextShipDate: nextAvailableDateString,
                addToCartVisible: true,
            };
        }

        return {
            allowAddToCart: false,
            availabilityLabel: t('addToCartBtn.not-available'),
            nextShipDate: null,
            addToCartVisible: true,
        };
    }, [
        currentLocale,
        inventory,
        isDisplaySingle,
        product.attributes?.custom_made?.values,
        t,
    ]);
};

function roundToNextWeekday(nextAvailableDate: dayjs.Dayjs) {
    if (nextAvailableDate.day() === 0)
        //sunday
        nextAvailableDate = nextAvailableDate.add(1, 'days');
    else if (nextAvailableDate.day() === 6)
        //saturday
        nextAvailableDate = nextAvailableDate.add(2, 'days');
    return nextAvailableDate;
}

export const AddToCartButton = (props: {
    product: any;
    qty: number;
    label?: string;
    className: string;
    small?: boolean;
    setCartVisible?: boolean;
}) => {
    const { product, qty, label, className, small, setCartVisible } = props;
    const { t } = useTranslation('layout');
    const { addAlert } = useContext(feedbackContext);
    const [price] = useProductPrice(product.code ?? product.sku);
    const [inventory] = useProductInventory(product.code ?? product.sku);

    const [addProductToCart] = useAddProductsToCartAction();

    const { setQuickCartVisible, setQuickCartLoading } = useCartContext();

    /**
     * Disables button after inventory / next available date check
     */

    const { allowAddToCart, addToCartVisible } =
        useAvailabilityInfo(product);

    // Note: Use NBSP during SSR so that button retain correct height
    const buttonLabel = addToCartVisible
        ? label
            ? label
            : inventory?.stock?.status == 'in-stock'
            ? t('addToCartBtn.add-to-cart')
            : t('addToCartBtn.pre-order')
        : '\u00A0';

    /**
     * Handles the click on add to cart button
     *
     * @param event On button click
     * @param sku Product code
     * @param qty Quantity to add to cart
     */
    function addToCart(
        event: React.MouseEvent<HTMLElement>,
        sku: string,
        qty: number,
        setCartVisible?: boolean | undefined
    ) {
        event.stopPropagation();
        event.preventDefault();
        setQuickCartVisible(
            setCartVisible !== undefined ? setCartVisible : true
        );
        setQuickCartLoading(true);

        addProductToCart(sku, qty)
            .then(() => {
                // console.log(res);
                // GTM GA4
                pushAddToCartEvent({
                    id: sku,
                    price: price?.finalPrice.value,
                    currency: price?.finalPrice.currency,
                    qty: qty,
                });
                setQuickCartLoading(false);
            })
            .catch((error) => {
                console.log(error);
                addAlert({
                    message: error,
                    status: 'error',
                });
                setQuickCartLoading(false);
            });
    }

    return (
        <Button
            label=''
            onClick={(event: React.MouseEvent<HTMLElement>) =>
                addToCart(
                    event,
                    product.code ?? product.sku,
                    qty,
                    setCartVisible
                )
            }
            disabled={!allowAddToCart}
            className={`AddToCart ${className ? className : ''}`}
        >
            <React.Fragment>
                {small ? '+' : buttonLabel}
                <FontAwesomeIcon icon={faShoppingCart} />
            </React.Fragment>
        </Button>
    );
};

AddToCartButton.defaultProps = {
    qty: 1,
};
